import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { ArrowLeft, Globe, Save, Loader2 } from 'lucide-react';
import api from '../utils/api';
import { toast } from 'sonner';

const RegionalSettings = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState({
    date_format: 'DD/MM/YYYY',
    time_format: '24h',
    timezone: 'UTC'
  });

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const res = await api.get('/settings');
      setSettings({
        date_format: res.data.date_format || 'DD/MM/YYYY',
        time_format: res.data.time_format || '24h',
        timezone: res.data.timezone || 'UTC'
      });
    } catch (error) {
      toast.error('Failed to load regional settings');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.put('/settings', settings);
      toast.success('Regional settings saved successfully');
    } catch (error) {
      toast.error('Failed to save regional settings');
    } finally {
      setSaving(false);
    }
  };

  const dateFormatOptions = [
    { value: 'DD/MM/YYYY', label: 'DD/MM/YYYY (31/12/2025)', region: 'UK, Europe, Australia' },
    { value: 'MM/DD/YYYY', label: 'MM/DD/YYYY (12/31/2025)', region: 'US' },
    { value: 'YYYY-MM-DD', label: 'YYYY-MM-DD (2025-12-31)', region: 'ISO 8601' },
    { value: 'DD MMM YYYY', label: 'DD MMM YYYY (31 Dec 2025)', region: 'Readable' }
  ];

  const timeFormatOptions = [
    { value: '12h', label: '12-hour (1:30 PM)' },
    { value: '24h', label: '24-hour (13:30)' }
  ];

  const timezoneOptions = [
    { value: 'UTC', label: 'UTC (Coordinated Universal Time)' },
    { value: 'Europe/London', label: 'Europe/London (GMT/BST)' },
    { value: 'Europe/Paris', label: 'Europe/Paris (CET/CEST)' },
    { value: 'America/New_York', label: 'America/New_York (EST/EDT)' },
    { value: 'America/Los_Angeles', label: 'America/Los_Angeles (PST/PDT)' },
    { value: 'America/Chicago', label: 'America/Chicago (CST/CDT)' },
    { value: 'Asia/Tokyo', label: 'Asia/Tokyo (JST)' },
    { value: 'Asia/Dubai', label: 'Asia/Dubai (GST)' },
    { value: 'Australia/Sydney', label: 'Australia/Sydney (AEST/AEDT)' }
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="w-8 h-8 animate-spin text-purple-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-blue-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 shadow-sm sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <Button
                variant="outline"
                size="sm"
                onClick={() => navigate('/settings')}
                className="border-slate-300"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back
              </Button>
              <div className="flex items-center gap-3">
                <div className="p-2 bg-gradient-to-br from-teal-500 to-teal-600 rounded-lg shadow-lg">
                  <Globe className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h1 className="text-2xl font-bold text-slate-800">Regional Settings</h1>
                  <p className="text-sm text-slate-600">Configure date format, timezone & localization</p>
                </div>
              </div>
            </div>
            <Button 
              onClick={handleSave} 
              disabled={saving}
              className="bg-gradient-to-r from-teal-500 to-teal-600 hover:from-teal-600 hover:to-teal-700"
            >
              {saving ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="w-4 h-4 mr-2" />
                  Save Settings
                </>
              )}
            </Button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Card>
          <CardHeader>
            <CardTitle>Date & Time Preferences</CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Date Format */}
            <div className="space-y-2">
              <Label htmlFor="date_format" className="text-base font-medium">
                Date Format
              </Label>
              <p className="text-sm text-gray-500">
                Choose how dates should be displayed throughout the application
              </p>
              <select
                id="date_format"
                value={settings.date_format}
                onChange={(e) => setSettings({ ...settings, date_format: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
              >
                {dateFormatOptions.map(option => (
                  <option key={option.value} value={option.value}>
                    {option.label} - {option.region}
                  </option>
                ))}
              </select>
            </div>

            {/* Time Format */}
            <div className="space-y-2">
              <Label htmlFor="time_format" className="text-base font-medium">
                Time Format
              </Label>
              <p className="text-sm text-gray-500">
                Choose between 12-hour or 24-hour time format
              </p>
              <select
                id="time_format"
                value={settings.time_format}
                onChange={(e) => setSettings({ ...settings, time_format: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
              >
                {timeFormatOptions.map(option => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>

            {/* Timezone */}
            <div className="space-y-2">
              <Label htmlFor="timezone" className="text-base font-medium">
                Timezone
              </Label>
              <p className="text-sm text-gray-500">
                Set your local timezone for accurate date/time display
              </p>
              <select
                id="timezone"
                value={settings.timezone}
                onChange={(e) => setSettings({ ...settings, timezone: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
              >
                {timezoneOptions.map(option => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>

            {/* Preview */}
            <div className="mt-6 p-4 bg-gray-50 rounded-lg border border-gray-200">
              <h3 className="font-medium text-gray-700 mb-2">Preview</h3>
              <div className="space-y-1 text-sm">
                <p className="text-gray-600">
                  <span className="font-medium">Date:</span>{' '}
                  {new Date().toLocaleDateString('en-GB', {
                    day: '2-digit',
                    month: '2-digit',
                    year: 'numeric',
                    timeZone: settings.timezone
                  }).split('/').join(settings.date_format.includes('MM/DD') ? '/' : '/')}
                </p>
                <p className="text-gray-600">
                  <span className="font-medium">Time:</span>{' '}
                  {new Date().toLocaleTimeString('en-GB', {
                    hour12: settings.time_format === '12h',
                    hour: '2-digit',
                    minute: '2-digit',
                    timeZone: settings.timezone
                  })}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </main>
    </div>
  );
};

export default RegionalSettings;
